import type { IIncidentReportActivityObservationData } from '@/models/report'
import type { AxiosInstance } from 'axios'
import axiosRetry from 'axios-retry'

import axios, { isAxios401Error } from '@/axios'
import { objectToFormData } from '@/utils/helpers'

import type { IIncidentReportActivityObservationService } from './incident-report-activity-observation.types'
/**
 * A class representing a IncidentReportActivityService API handler.
 */
export class IncidentReportActivityObservationService
  implements IIncidentReportActivityObservationService
{
  private endpoint: string

  // IncidentReportActivityService constructor
  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/reports/incidents/'
  }

  // Method to fetch a list of reports from the API
  fetchIncidentReportActivityObservations(
    reportId: number,
    activityId: number
  ): Promise<IIncidentReportActivityObservationData[]> {
    return this.axios
      .get(`${this.endpoint}${reportId}/activities/${activityId}/observations/`)
      .then(({ data }) => data)
  }

  // Method to fetch a single report by their ID from the API
  fetchIncidentReportActivityObservation(
    reportId: number,
    activityId: number,
    id: number
  ): Promise<IIncidentReportActivityObservationData> {
    return this.axios
      .get(`${this.endpoint}${reportId}/activities/${activityId}/observations/${id}/`)
      .then(({ data }) => data)
  }

  // Method to create a new report on the API
  createIncidentReportActivityObservation(
    reportId: number,
    activityId: number,
    data: IIncidentReportActivityObservationData
  ): Promise<IIncidentReportActivityObservationData> {
    const formData = objectToFormData(data)
    return this.axios
      .post(`${this.endpoint}${reportId}/activities/${activityId}/observations/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // LF-2023-01-13: Hack to ensure file is set on token refresh (if required).
        // Required if 401 occurred while handling multipart/form-data
        'axios-retry': {
          retries: 1,
          retryCondition: isAxios401Error || axiosRetry.isSafeRequestError
        }
      })
      .then(({ data }) => data)
  }

  // Method to update an existing report on the API
  updateIncidentReportActivityObservation(
    reportId: number,
    activityId: number,
    data: IIncidentReportActivityObservationData
  ): Promise<IIncidentReportActivityObservationData> {
    const formData = objectToFormData(data)
    return this.axios
      .patch(
        `${this.endpoint}${reportId}/activities/${activityId}/observations/${data.id}/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          // LF-2023-01-13: Hack to ensure file is set on token refresh (if required).
          // Required if 401 occurred while handling multipart/form-data
          'axios-retry': {
            retries: 1,
            retryCondition: isAxios401Error || axiosRetry.isSafeRequestError
          }
        }
      )
      .then(({ data }) => data)
  }

  // Method to delete a report by their ID from the API
  deleteIncidentReportActivityObservation(
    reportId: number,
    activityId: number,
    id: number
  ): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${reportId}/activities/${activityId}/observations/${id}/`)
      .then(({ data }) => data)
  }
}

// Default export instantiation of the IncidentReportActivityObservationService
const activityIncidentReportService = new IncidentReportActivityObservationService(axios)
export default activityIncidentReportService
